import { formsService } from '@services/domain-factories/forms';
import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { permissionsService } from '@services/permissions';
import { hasItems } from '@utils/vanillaJs';
import { Form, Tag } from '@vincerosoft/assessfluence-types';
import { arrayRemove, arrayUnion, or, QueryConstraint, where } from 'firebase/firestore';
import { accountStore } from 'src/stores/bootstrap-stores/account';

class TagsFactory {
  getTagsByFormIds = async (field: 'form_ids' | 'form_group_ids', ids: string[]) => {
    if (!ids?.length) {
      return [];
    }
    const tagsArr = await collectionsService.tags.getDocsInBatches([
      field,
      'array-contains-any',
      ids
    ]);
    return tagsArr.flat();
  };

  async loadTagsForForms(ids: { forms?: string[]; formGroups?: string[] }) {
    if (permissionsService.isTenant) {
      const tags = await collectionsService.tags.getDocs([
        or(
          where('is_tenant', '==', true),
          where('domain', '==', 'TENANT')
        ) as unknown as QueryConstraint
      ]);
      return tags.filter((tag) => tag.context === 'studio');
    }

    let tags: Tag[] = [];

    if (hasItems(ids?.forms)) {
      const formTags = await this.getTagsByFormIds('form_ids', ids.forms || []);
      tags = [...tags, ...formTags];
    }
    if (hasItems(ids?.formGroups)) {
      const formGroupTags = await this.getTagsByFormIds('form_group_ids', ids.formGroups || []);
      tags = [...tags, ...formGroupTags];
    }

    return tags.filter((tag) => tag.context === 'studio');
  }

  loadEntityTags = async () => {
    const { scopeQuery } = accountStore.getState();

    const tags = await collectionsService.tags.getDocs(scopeQuery);
    return tags.flat().filter((tag) => tag.context === 'entities');
  };

  updateTags = async (
    field: 'form_ids' | 'form_group_ids' | 'campaign_ids' | 'entity_ids',
    sourceId: string,
    currTags: string[],
    modifiedTags: string[]
  ) => {
    const removedIds = currTags?.length
      ? currTags.length && currTags.filter((tagId) => !modifiedTags.includes(tagId))
      : [];

    const addedIds =
      modifiedTags?.length && modifiedTags.filter((tagId) => !currTags.includes(tagId));

    if (!hasItems(addedIds) && !hasItems(removedIds)) {
      return;
    }

    const addPromises = hasItems(addedIds)
      ? addedIds.map((tagId) =>
          collectionsService.tags.updateDoc(tagId, {
            [field]: arrayUnion(sourceId)
          })
        )
      : [];

    const removePromises = hasItems(removedIds)
      ? removedIds.map((tagId) =>
          collectionsService.tags.updateDoc(tagId, {
            [field]: arrayRemove(sourceId)
          })
        )
      : [];

    await Promise.all([...addPromises, ...removePromises]);
  };

  bulkAddTagsToSources = async (
    field: 'form_ids' | 'form_group_ids' | 'campaign_ids' | 'entity_ids',
    sourceIds: string[],
    tagIds: string[]
  ) => {
    const uniqueSourceIds = Array.from(new Set((sourceIds || []).filter(Boolean)));
    const uniqueTagIds = Array.from(new Set((tagIds || []).filter(Boolean)));

    if (!hasItems(uniqueSourceIds) || !hasItems(uniqueTagIds)) {
      return;
    }

    const tagSourceProp = field === 'form_ids' ? 'form_ids' : 'form_group_ids';

    const sourceUpdatePromises =
      field === 'form_ids'
        ? uniqueSourceIds.map((sourceId) =>
            formsService.updateForm(sourceId, {
              'options.tags': arrayUnion(...uniqueTagIds)
            } as Partial<Form>)
          )
        : field === 'form_group_ids'
          ? uniqueSourceIds.map((sourceId) =>
              collectionsService.formGroups.updateDoc(sourceId, {
                'options.tags': arrayUnion(...uniqueTagIds)
              })
            )
          : field === 'campaign_ids'
            ? uniqueSourceIds.map((sourceId) =>
                collectionsService.campaigns.updateDoc(sourceId, {
                  tags: arrayUnion(...uniqueTagIds)
                })
              )
            : uniqueSourceIds.map((sourceId) =>
                collectionsService.entities.updateDoc(sourceId, {
                  'options.tags': arrayUnion(...uniqueTagIds)
                })
              );

    await Promise.all(sourceUpdatePromises);

    await Promise.all(
      uniqueTagIds.map((tagId) =>
        collectionsService.tags.updateDoc(tagId, {
          [tagSourceProp]: arrayUnion(...uniqueSourceIds)
        })
      )
    );
  };

  async loadTagsForCampaigns(campaignIds?: string[], onProcessBatch?: (tags: Tag[]) => void) {
    const tags = await collectionsService.tags.getDocsInBatches(
      ['campaign_ids', 'in', campaignIds || []],
      undefined,
      onProcessBatch
    );
    return tags.flat().filter((tag) => tag.context === 'campaigns');
  }
}

export const tagsService = new TagsFactory();
